/**
 * twilioDevice — types + helpers partagés pour le Twilio Voice SDK.
 *
 * Phase 4 bis : remplace les `cast (window as unknown as { __twilioDevice }).__twilioDevice`
 * disséminés dans les composants TSX. Le device lui-même reste initialisé par
 * DialerContext.jsx (useEffect complex, auto-refresh token, AMD webhook...) —
 * mais les consommateurs qui veulent muter / hangup / sendDigits accèdent
 * désormais via `getTwilioDevice()` typé.
 *
 * Une future étape (Phase 5) extraira l'initialisation dans ce module.
 */

/** Shape partielle d'un Twilio Call (SDK Voice v2) */
export interface TwilioCall {
  sid?: string;
  status(): string;
  mute(state: boolean): void;
  isMuted(): boolean;
  disconnect(): void;
  sendDigits(digits: string): void;
  accept(options?: unknown): void;
  reject(): void;
  ignore(): void;
  on(event: string, cb: (...args: unknown[]) => void): void;
  customParameters?: Map<string, string>;
  parameters?: { From?: string; To?: string; CallSid?: string };
}

/** Shape partielle du Twilio Device (SDK Voice v2) */
export interface TwilioDevice {
  /** An array in SDK v2 (`get calls(): Call[]`). Read it through listDeviceCalls. */
  calls?: TwilioCall[];
  updateToken(token: string): void;
  register(): void;
  destroy(): void;
  on(event: string, cb: (...args: unknown[]) => void): void;
  connect(options: { params: Record<string, string | number> }): Promise<TwilioCall>;
}

declare global {
  interface Window {
    __twilioDevice?: TwilioDevice | null;
  }
}

/**
 * The device's calls as an array. SDK v2 exposes `calls` as an array; every
 * `.calls.size > 0` check in the app read `undefined` and skipped its branch
 * (mute fallback, DTMF, browser-leg disconnect on Stop). BUGS-OUVERTS DEV-1.
 * A Map or Set (older mocks) is read by its values.
 */
export function listDeviceCalls<T = TwilioCall>(dev: { calls?: unknown } | null | undefined): T[] {
  const calls = dev?.calls as unknown;
  if (!calls) return [];
  if (Array.isArray(calls)) return calls as T[];
  if (calls instanceof Map || calls instanceof Set) return Array.from(calls.values()) as T[];
  return [];
}

/**
 * Hangs up the device's calls, except a call still ringing in (`pending`:
 * not yet answered) and the ones listed in `keep` (the standing conference).
 * The SDK adds an incoming call to `calls` before it emits 'incoming', and
 * the incoming-ringing handler stops the session: without this guard, Stop
 * hung up the incoming call it was making room for.
 */
export function disconnectDeviceCalls(dev: { calls?: unknown } | null | undefined, keep: unknown[] = []): void {
  for (const call of listDeviceCalls<{ status?: () => string; disconnect?: () => void }>(dev)) {
    if (keep.includes(call)) continue;
    let status = '';
    try { status = typeof call.status === 'function' ? call.status() : ''; } catch { /* unknown */ }
    if (status === 'pending') continue;
    try { call.disconnect?.(); } catch { /* per-call swallow */ }
  }
}

/** Retourne le device actif, ou null s'il n'est pas encore registered. */
export function getTwilioDevice(): TwilioDevice | null {
  return window.__twilioDevice ?? null;
}

/** Itère les calls actifs avec un handler typé. */
export function forEachActiveCall(fn: (call: TwilioCall) => void): void {
  listDeviceCalls(getTwilioDevice()).forEach(fn);
}

/** Mute/unmute tous les calls actifs en swallowant les erreurs par-call. */
export function muteAllActiveCalls(muted: boolean): void {
  forEachActiveCall((call) => {
    try {
      call.mute(muted);
    } catch {
      /* per-call swallow */
    }
  });
}

/** Envoie un digit DTMF sur tous les calls actifs. */
export function sendDTMFToAllActiveCalls(digit: string): void {
  forEachActiveCall((call) => {
    try {
      call.sendDigits(digit);
    } catch {
      /* per-call swallow */
    }
  });
}

/** Raccroche tous les calls actifs (local disconnect). */
export function disconnectAllActiveCalls(): void {
  forEachActiveCall((call) => {
    try {
      call.disconnect();
    } catch {
      /* per-call swallow */
    }
  });
}

/** Combien de calls actifs ont une session ouverte côté SDK. */
export function getActiveCallCount(): number {
  return listDeviceCalls(getTwilioDevice()).length;
}
